import LazyImage from 'components/Image/LazyImage';
import noImage from 'components/Image/noIMG.svg';
import QuantityInput from 'components/MarketItem/QuantityInput/QuantityInput';
import { mapProductToAiMatched } from 'components/QuickOrder/ChangeProductModal';
import { AnalyzedItem } from 'containers/QuickOrder/types';
import { formatVND } from 'containers/QuickOrder/utils';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { ChangeEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styles/styled-components';
import translations from 'translations';
import { AiMatchedProduct, TrackingAddToCartAddFromType } from 'types/schema';
import AccurateBadge from './AccurateBadge';
import { SearchIcon } from './icons';
import messages from './messages';
import { useProductSearch } from './useProductSearch';

interface ResultRowProps {
  product: AnalyzedItem;
  quantity: number;
  onToggle: (id: string) => void;
  onQuantityChange: (id: string, value: number) => void;
  onChangeProduct: (id: string, anchor: HTMLElement) => void;
  onSwapProduct: (id: string, alt: AiMatchedProduct) => void;
  loggedIn: boolean;
  goToSignIn: () => void;
}

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 16px;
`;

const Checkbox = styled.button<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${({ checked }) => (checked ? '#43A047' : '#D1D5DB')};
  background: ${({ checked }) => (checked ? '#43A047' : '#fff')};
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &::after {
    content: '';
    display: ${({ checked }) => (checked ? 'block' : 'none')};
    width: 4px;
    height: 8px;
    border: solid #fff;
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg) translate(-1px, -1px);
  }
`;

const ProductCol = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 310px;
  flex-shrink: 0;
`;

const Thumb = styled(({ noImage: _noImage, ...rest }) => <LazyImage {...rest} />)<{ noImage: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  object-fit: ${({ noImage: noImg }) => (noImg ? 'contain' : 'cover')};
  ${({ noImage: noImg }) => (noImg ? 'opacity: 0.3;' : '')}
  flex-shrink: 0;
  background: #f5f5f5;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #111827;
`;

const UnitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Unit = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #4b5563;
`;

const PurchasedTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 4px;
  border-radius: 4px;
  background: #f2f3f5;
  color: #303643;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

const UserInputText = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #6b7280;
`;

const ChangeBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #3c9041;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  align-self: flex-start;

  &:hover {
    opacity: 0.8;
  }
`;

const Price = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #111827;
  width: 100px;
  flex-shrink: 0;
  text-align: left;
`;

const ConfidenceCol = styled.div`
  width: 106px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const QuantityCol = styled.div`
  width: 140px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InlineSearchWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InlineSearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #fff;
`;

const InlineSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #111827;
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: #bdbdbd;
  }
`;

const InlineDropdown = styled.div<{ top: number; left: number }>`
  position: fixed;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: 320px;
  background: #fff;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  z-index: 1002;
  max-height: 242px;
  overflow-y: auto;
`;

const DropdownRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: #fff;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f5f5f5;
  }
`;

const DropdownThumb = styled(({ noImage: _noImage, ...rest }) => <LazyImage {...rest} />)<{ noImage: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  object-fit: ${({ noImage: noImg }) => (noImg ? 'contain' : 'cover')};
  ${({ noImage: noImg }) => (noImg ? 'opacity: 0.3;' : '')}
  flex-shrink: 0;
  background: #f5f5f5;
`;

const DropdownInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownName = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #333333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownUnit = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #9e9e9e;
  margin-top: 2px;
`;

const DropdownPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #333333;
  flex-shrink: 0;
`;

const UnmatchedRow = ({
  product,
  onToggle,
  onSwapProduct,
}: Pick<ResultRowProps, 'product' | 'onToggle' | 'onSwapProduct'>) => {
  const handleToggle = useCallback(() => onToggle(product.id), [product.id, onToggle]);

  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { results: searchResults, searchProducts, clearResults } = useProductSearch();
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const closeSearch = useCallback(() => {
    setIsSearching(false);
    setSearchQuery('');
    clearResults();
  }, [clearResults]);

  const getDropdownPos = useCallback(() => {
    const rect = searchWrapperRef.current?.getBoundingClientRect();
    return { top: rect ? rect.bottom + 6 : 0, left: rect ? rect.left : 0 };
  }, []);

  const [dropdownPos, setDropdownPos] = useState(getDropdownPos);

  useEffect(() => {
    if (!isSearching) return;
    const update = () => setDropdownPos(getDropdownPos());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isSearching, getDropdownPos]);

  useEffect(() => {
    if (!isSearching) return;
    const handleMouseDown = (e: MouseEvent) => {
      const inWrapper = searchWrapperRef.current?.contains(e.target as Node);
      const inDropdown = dropdownRef.current?.contains(e.target as Node);
      if (!inWrapper && !inDropdown) closeSearch();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isSearching, closeSearch]);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setSearchQuery(val);
      searchProducts(val);
    },
    [searchProducts],
  );

  const handleSelectSearchResult = useCallback(
    (r) => {
      onSwapProduct(product.id, mapProductToAiMatched(r));
      closeSearch();
    },
    [product.id, onSwapProduct, closeSearch],
  );

  const userInputText = translations(messages.userInput).replace('{input}', product.inputText);

  return (
    <Row>
      <Checkbox checked={false} onClick={handleToggle} aria-label="select" style={{ visibility: 'hidden' }} />
      <ProductCol style={{ paddingLeft: 64 }}>
        <Info>
          <UserInputText>{userInputText}</UserInputText>
          {isSearching ? (
            <InlineSearchWrapper ref={searchWrapperRef}>
              <InlineSearchBar>
                <SearchIcon />
                <InlineSearchInput
                  autoFocus
                  placeholder={translations(messages.searchPlaceholder)}
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </InlineSearchBar>
              {searchResults.length > 0 && (
                <InlineDropdown ref={dropdownRef} top={dropdownPos.top} left={dropdownPos.left}>
                  {searchResults.map((r) => (
                    <DropdownRow key={r.id} type="button" onClick={() => handleSelectSearchResult(r)}>
                      <DropdownThumb noImage={!r.imageUrl} src={r.imageUrl || noImage} alt={r.name ?? ''} />
                      <DropdownInfo>
                        <DropdownName>{r.name}</DropdownName>
                        <DropdownUnit>{r.uomLocal || r.uom}</DropdownUnit>
                      </DropdownInfo>
                      <DropdownPrice>{formatVND(r.price)}</DropdownPrice>
                    </DropdownRow>
                  ))}
                </InlineDropdown>
              )}
            </InlineSearchWrapper>
          ) : (
            <ChangeBtn type="button" onClick={() => setIsSearching(true)}>
              {translations(messages.searchManually)}
            </ChangeBtn>
          )}
        </Info>
      </ProductCol>
      <Price />
      <ConfidenceCol>
        <AccurateBadge level="none" />
      </ConfidenceCol>
      <QuantityCol />
    </Row>
  );
};

const MatchedRow = ({
  product,
  quantity,
  onToggle,
  onQuantityChange,
  onChangeProduct,
  loggedIn,
  goToSignIn,
}: Omit<ResultRowProps, 'onSwapProduct'>) => {
  const p = product.matchedProduct ?? null;
  const changeBtnRef = useRef<HTMLButtonElement>(null);
  const { uomInfo } = useProductUomInfo(p ?? ({} as AiMatchedProduct));

  const handleToggle = useCallback(() => onToggle(product.id), [product.id, onToggle]);
  const handleChangeProduct = useCallback(() => {
    if (changeBtnRef.current) onChangeProduct(product.id, changeBtnRef.current);
  }, [product.id, onChangeProduct]);
  const handleQuantity = useCallback(
    (value: number) => onQuantityChange(product.id, value),
    [product.id, onQuantityChange],
  );

  const userInputText = translations(messages.userInput).replace('{input}', product.inputText);

  if (!p) return null;

  return (
    <Row>
      <Checkbox checked={product.selected} onClick={handleToggle} aria-label="select" />
      <ProductCol>
        <Thumb noImage={!p.imageUrl} src={p.imageUrl || noImage} alt={p.name} />
        <Info>
          <Name>{p.name}</Name>
          <UnitRow>
            <Unit>{uomInfo}</Unit>
            {product.fromHistory && <PurchasedTag>{translations(messages.purchasedTag)}</PurchasedTag>}
          </UnitRow>
          <UserInputText>{userInputText}</UserInputText>
          <ChangeBtn ref={changeBtnRef} type="button" onClick={handleChangeProduct}>
            {translations(messages.changeProduct)}
          </ChangeBtn>
        </Info>
      </ProductCol>
      <Price>{formatVND(p.price)}</Price>
      <ConfidenceCol>
        <AccurateBadge level={product.confidence} />
      </ConfidenceCol>
      <QuantityCol>
        <QuantityInput
          loggedIn={loggedIn}
          productId={p.sku}
          min={1}
          max={999}
          value={quantity}
          onChange={handleQuantity}
          allowDecimal={false}
          orderDisabled={false}
          goToSignIn={goToSignIn}
          addFrom={TrackingAddToCartAddFromType.AllProduct}
        />
      </QuantityCol>
    </Row>
  );
};

const ResultRow = (props: ResultRowProps) => {
  if (!props.product.matchedProduct) {
    return <UnmatchedRow product={props.product} onToggle={props.onToggle} onSwapProduct={props.onSwapProduct} />;
  }
  return <MatchedRow {...props} />;
};

export default memo(ResultRow);
